/*
 * 포트빌 입주 신청 페이지 (PortvillePage)
 * 
 * ⚠️  임시 접근 제한 적용됨 ⚠️
 * 
 * 현재 이 페이지는 순차 오픈을 위해 특정 권한의 사용자만 접근할 수 있도록 제한되어 있습니다.
 * 
 * 제한 조건:
 * - userLevel 4 이하: 접근 허용 (공사 관계자, 센터 관계자, 시스템 관리자)
 * - userLevel 5 이상: 접근 제한 → /site-preparation 페이지로 리다이렉트
 * 
 * 정식 오픈 시 해제 방법:
 * 1. TEMPORARY_ACCESS_CONTROL 상수를 false로 변경
 * 2. 관련 주석 및 코드 블록 제거
 * 3. 사용자 인증 로직을 원래대로 복원
 * 
 * 검색 키워드: "임시 접근 제한", "순차 오픈용", "TEMPORARY_ACCESS_CONTROL"
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { useWaitingApplications } from '../../hooks/useWaitingApplications';
import { useAuth } from '../../hooks/useAuth';
import { useEmailVerification } from '../../hooks/useEmailVerification';
import { db } from '../../firebase';
import { format } from 'date-fns';
import ClientLayout from '../../components/layout/ClientLayout';
import ResidencyHistorySection, {
  type ResidencyHistoryItem
} from './portville/components/ResidencyHistorySection';

// ===== 임시 접근 제한 설정 (순차 오픈용) =====
// TODO: 사이트 정식 오픈 시 이 플래그를 false로 변경하여 접근 제한을 해제하세요
const TEMPORARY_ACCESS_CONTROL = false;
// 허용되는 최대 userLevel (4 이하만 접근 가능, 5 이상은 제한)
const MAX_ALLOWED_USER_LEVEL = 4;
// ============================================

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}년 ${month}월 ${day}일`;
};

type UserPortvilleStage = 'none' | 'application' | 'lottery' | 'residency' | 'checking';

const PortvillePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { waitingList, loading: waitingLoading, error } = useWaitingApplications();
  const { loading: emailVerificationLoading } = useEmailVerification();
  const [userPortvilleStage, setUserPortvilleStage] = useState<UserPortvilleStage>('checking');
  const [lotteryStatus, setLotteryStatus] = useState<'drawn' | 'contracted' | null>(null);
  const [residencyHistory, setResidencyHistory] = useState<ResidencyHistoryItem[]>([]);
  const [terminalAnchorMillis, setTerminalAnchorMillis] = useState(0);
  // ===== 임시 접근 제한 관련 state (순차 오픈용) =====
  // TODO: 접근 제한 해제 시 이 state와 관련 로직도 함께 제거하세요
  const [accessCheckComplete, setAccessCheckComplete] = useState(false);
  // ============================================
  // 사용자 프로필 실시간 리스너용 state
  const [userProfileData, setUserProfileData] = useState<any>(null);
  const [userProfileLoading, setUserProfileLoading] = useState(true);

  // portville_applications 자동 연결 함수
  const linkPortvilleApplications = async (user: any, userPhoneNumber: string) => {
    try {
      const applicationsRef = collection(db, 'portville_applications');
      const q = query(
        applicationsRef,
        where('status', 'in', ['pending', 'approved']),
        where('phoneNumber', '==', userPhoneNumber)
      );
      
      const querySnapshot = await getDocs(q);
      
      // uid가 비어있는 문서 찾기
      const emptyUidApplications = querySnapshot.docs.filter(doc => {
        const data = doc.data();
        return !data.uid || data.uid.trim() === '';
      });
      
      // 매칭되는 신청서가 있으면 uid 업데이트
      if (emptyUidApplications.length > 0) {
        const applicationDoc = emptyUidApplications[0]; // 첫 번째 매칭 신청서 사용
        
        // 사용자 프로필 정보 가져오기
        const userProfileRef = doc(db, 'user_profiles', user.uid);
        const userProfileSnap = await getDoc(userProfileRef);
        
        if (userProfileSnap.exists()) {
          const userData = userProfileSnap.data();
          
          // 신청서 업데이트
          await updateDoc(doc(db, 'portville_applications', applicationDoc.id), {
            uid: user.uid,
            name: userData.name || "",
            email: user.email || "",
            phoneNumber: userData.phoneNumber || "",
            organization: userData.organization || "",
            userLevel: userData.userLevel || 9,
            updatedAt: serverTimestamp()
          });
          
          console.log('portville_applications 자동 연결 완료:', applicationDoc.id);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('portville_applications 자동 연결 중 오류:', error);
      return false;
    }
  };

  // 기존 신청자 대기전환 버튼 클릭 핸들러
  const handleExistingApplicationTransfer = async () => {
    if (!user || !userProfileData || !userProfileData.phoneNumber) {
      alert('사용자 정보를 불러올 수 없습니다.');
      return;
    }

    try {
      const applicationsRef = collection(db, 'portville_applications');
      
      // 1. 자신의 전화번호와 동일한 phoneNumber를 가지면서 uid가 비어있는 신청서 찾기
      const emptyUidQuery = query(
        applicationsRef,
        where('phoneNumber', '==', userProfileData.phoneNumber),
        where('status', 'in', ['pending', 'approved'])
      );
      
      const emptyUidSnapshot = await getDocs(emptyUidQuery);
      const emptyUidApplications = emptyUidSnapshot.docs.filter(doc => {
        const data = doc.data();
        return !data.uid || data.uid.trim() === '';
      });

      // 2. 자신의 uid로 된 신청서 찾기
      const userUidQuery = query(
        applicationsRef,
        where('uid', '==', user.uid)
      );
      const userUidSnapshot = await getDocs(userUidQuery);

      // 3. 처리 로직
      if (emptyUidApplications.length > 0) {
        // 1번 케이스: uid가 비어있는 신청서에 정보 갱신
        const applicationDoc = emptyUidApplications[0];
        const userProfileRef = doc(db, 'user_profiles', user.uid);
        const userProfileSnap = await getDoc(userProfileRef);
        
        if (userProfileSnap.exists()) {
          const userData = userProfileSnap.data();
          
          await updateDoc(doc(db, 'portville_applications', applicationDoc.id), {
            uid: user.uid,
            name: userData.name || "",
            email: user.email || "",
            phoneNumber: userData.phoneNumber || "",
            organization: userData.organization || "",
            userLevel: userData.userLevel || 9,
            updatedAt: serverTimestamp()
          });
          
          alert('신청서 정보가 업데이트되었습니다.');
          // 실시간 리스너가 자동으로 업데이트하므로 페이지 새로고침 불필요
        }
      } else if (!userUidSnapshot.empty) {
        // 2번 케이스: 자신의 uid로 된 신청서가 있음
        const confirmed = window.confirm('신청서가 존재합니다. 확인하시겠습니까?');
        if (confirmed) {
          navigate('/portville/detail');
        }
      } else {
        // 3번 케이스: 둘 다 해당되지 않음
        alert('신청 정보가 존재하지 않습니다.');
      }
    } catch (error) {
      console.error('기존 신청자 대기전환 처리 중 오류:', error);
      alert('처리 중 오류가 발생했습니다.');
    }
  };

  // 사용자 프로필 실시간 리스너 설정
  useEffect(() => {
    if (!user) {
      setUserProfileData(null);
      setUserProfileLoading(false);
      return;
    }

    setUserProfileLoading(true);
    const userProfileRef = doc(db, 'user_profiles', user.uid);
    
    // Firestore 실시간 리스너 설정
    const unsubscribe = onSnapshot(
      userProfileRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const profileData = snapshot.data();
          setUserProfileData(profileData);
          
          // ===== 임시 접근 제한 체크 (순차 오픈용) =====
          if (TEMPORARY_ACCESS_CONTROL) {
            const userLevel = profileData.userLevel || 999; // 기본값: 999 (최고 제한)
            
            // userLevel이 MAX_ALLOWED_USER_LEVEL보다 큰 경우 접근 제한
            if (userLevel > MAX_ALLOWED_USER_LEVEL) {
              console.log(`접근 제한: userLevel ${userLevel} > ${MAX_ALLOWED_USER_LEVEL}`);
              navigate('/site-preparation');
              return;
            }
          }
          // ============================================
        } else {
          // 사용자 프로필이 없는 경우
          setUserProfileData(null);
          if (TEMPORARY_ACCESS_CONTROL) {
            console.log('접근 제한: 사용자 프로필 없음');
            navigate('/site-preparation');
            return;
          }
        }
        setUserProfileLoading(false);
      },
      (error) => {
        console.error('사용자 프로필 실시간 업데이트 오류:', error);
        setUserProfileLoading(false);
        if (TEMPORARY_ACCESS_CONTROL) {
          navigate('/site-preparation');
        }
      }
    );

    return () => unsubscribe();
  }, [user, navigate]);

  // portville_applications 자동 연결 (사용자 프로필이 로드되고 전화번호가 있는 경우)
  useEffect(() => {
    if (!user || !userProfileData || userProfileLoading || authLoading) return;

    const linkApplications = async () => {
      if (userProfileData.phoneNumber) {
        try {
          await linkPortvilleApplications(user, userProfileData.phoneNumber);
        } catch (error) {
          console.error('portville_applications 자동 연결 실패:', error);
        }
      }
    };

    linkApplications();
  }, [user, userProfileData, userProfileLoading, authLoading]);

  // 사용자 인증 상태 및 권한 확인
  useEffect(() => {
    const checkUserAccess = async () => {
      if (authLoading || userProfileLoading) return;

      if (!user) {
        navigate('/signin');
        return;
      }

      // 접근 제한 체크를 통과한 경우 accessCheckComplete를 true로 설정
      setAccessCheckComplete(true);
    };

    checkUserAccess();
  }, [user, authLoading, userProfileLoading, navigate]);

  // 접근 제한 체크 통과 후 residency status 체크
  useEffect(() => {
    if (accessCheckComplete && user && !authLoading && !emailVerificationLoading) {
      checkUserPortvilleStage();
    }
  }, [accessCheckComplete, user, authLoading, emailVerificationLoading]);

	  const checkUserPortvilleStage = async () => {
		    if (!user) return;
		    
		    try {
		      setUserPortvilleStage('checking');
		      setLotteryStatus(null);
	        setResidencyHistory([]);
	        setTerminalAnchorMillis(0);

	        const getMillis = (value: any): number => {
	          try {
	            if (!value) return 0;
	            if (typeof value.toMillis === 'function') return value.toMillis();
	            if (typeof value.toDate === 'function') return value.toDate().getTime();
	            if (value instanceof Date) return value.getTime();
	            return new Date(value).getTime();
	          } catch {
	            return 0;
	          }
	        };
	        let lastTerminalMillis = 0;

		      // 1. 새로운 데이터 구조에서 입주 기록 확인 (입주 기록을 최우선으로 확인)
		      const residenciesRef = collection(db, 'portville_residencies');
		      const residencyQuery = query(residenciesRef, where('residentUid', '==', user.uid));
		      const residencySnapshot = await getDocs(residencyQuery);
		      
		      if (!residencySnapshot.empty) {
		        const residencyDocs = residencySnapshot.docs.map((snap) => ({
	            id: snap.id,
	            ...snap.data()
	          })) as ResidencyHistoryItem[];

		          const terminal = residencyDocs
		            .filter((r) => r.status === 'completed' || r.status === 'terminated')
	            .sort((a, b) => {
		              const aKey =
		                getMillis((a as any).completedAt) ||
		                getMillis((a as any).terminatedAt) ||
		                getMillis((a as any).contractCancelledAt) ||
		                getMillis(a.contractSignedAt);
		              const bKey =
		                getMillis((b as any).completedAt) ||
		                getMillis((b as any).terminatedAt) ||
		                getMillis((b as any).contractCancelledAt) ||
		                getMillis(b.contractSignedAt);
	              return bKey - aKey;
	            });

	          setResidencyHistory(terminal);
	          lastTerminalMillis = terminal.reduce((acc, r) => {
	            // NOTE(policy): updatedAt은 관리자 이력 수정으로 흔들릴 수 있으므로 anchor에서 제외
	            const key =
	              getMillis((r as any).completedAt) ||
	              getMillis((r as any).terminatedAt) ||
	              getMillis((r as any).contractCancelledAt) ||
	              getMillis(r.contractSignedAt);
	            return Math.max(acc, key);
		          }, 0);
		          setTerminalAnchorMillis(lastTerminalMillis);

		          const getResidencyCycleMillis = (r: any) =>
		            getMillis(r?.updatedAt) || getMillis(r?.contractSignedAt) || getMillis(r?.createdAt);

		          const inProgress = residencyDocs
		            .filter((r) => {
		              if (!(r.status === 'active' || r.status === 'contracting')) return false;
		              return getResidencyCycleMillis(r) > lastTerminalMillis;
		            })
		            .sort((a, b) => getResidencyCycleMillis(b) - getResidencyCycleMillis(a));

		          const currentResidency = inProgress[0] ?? null;
		          if (currentResidency?.status === 'active' || currentResidency?.contractSignedAt) {
		            navigate('/portville/residency');
		            return;
		          }

		          if (currentResidency?.status === 'contracting') {
		            setLotteryStatus('drawn');
		            setUserPortvilleStage('lottery');
		            return;
		          }

	          // terminal만 존재하는 경우에는 자동 리다이렉트하지 않음 (재신청 가능)
		      }
		      
		      // 2. 추첨 기록 확인 (입주 기록이 진행중이 아닐 때)
		      const lotteryRef = collection(db, 'portville_lottery_history');
		      const lotteryQuery = query(lotteryRef, where('uid', '==', user.uid));
	      const lotterySnapshot = await getDocs(lotteryQuery);

	      if (!lotterySnapshot.empty) {
	        // NOTE(policy): "현재 진행" lottery는 status='drawn' && !archivedAt && !suppressedAt 인 문서만 인정한다.
	        // NOTE(policy): 최신/새 회차 기준은 drawnAt 단일로 통일한다.
	        const candidates = lotterySnapshot.docs
	          .map((d) => ({ id: d.id, data: d.data() as any }))
	          .filter((d) => {
	            const data = d.data;
	            if (data?.status !== 'drawn') return false;
	            if (data?.archivedAt) return false;
	            if (data?.suppressedAt) return false;
	            const drawnAtMillis = getMillis(data?.drawnAt);
	            return drawnAtMillis > lastTerminalMillis;
	          })
	          .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));

	        const latest = candidates[0]?.data ?? null;
	        const latestDrawnAtMillis = getMillis(latest?.drawnAt) || 0;

	        if (latest && latestDrawnAtMillis > lastTerminalMillis) {
	          setLotteryStatus('drawn');
	          setUserPortvilleStage('lottery');
	          return;
	        }
	      }

	      // 3. 신청 이력은 waitingList 기반으로 렌더 단계에서 결정
	      setUserPortvilleStage('none');
      
    } catch (error) {
      console.error('Error checking user portville stage:', error);
      setUserPortvilleStage('none');
    }
  };

  // ===== 로딩 조건 (임시 접근 제한 포함) =====
  // TODO: 접근 제한 해제 시 !accessCheckComplete 조건을 제거하세요
  if (authLoading || emailVerificationLoading || userProfileLoading || !accessCheckComplete || userPortvilleStage === 'checking') {
    return (
      <ClientLayout title="포트빌 입주 신청">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!user) {
    return null;
  }

  if (waitingLoading) {
    return (
      <ClientLayout title="포트빌 입주 신청">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout title="포트빌 입주 신청">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-red-500">{error}</div>
        </div>
      </ClientLayout>
    );
  }

  const userApplications = waitingList.filter(app => app.uid === user.uid);
  // NOTE: 과거 계약(완료/포기) 이전에 생성된 신청서는 "현재 회차"로 보지 않는다.
	  const currentCycleApplications = userApplications.filter((app) => {
	    if (!(app.status === 'pending' || app.status === 'checked' || app.status === 'approved')) return false;
	    const createdMillis = app.createdAt instanceof Date ? app.createdAt.getTime() : new Date(app.createdAt as any).getTime();
	    return createdMillis > terminalAnchorMillis;
	  });
  const hasApprovedApplication = currentCycleApplications.some(app => app.status === 'approved');
  const hasPendingApplication = currentCycleApplications.some(app => app.status === 'pending' || app.status === 'checked');

  const pendingApplications = waitingList.filter((application) => {
    if (!(application.status === 'pending' || application.status === 'checked')) return false;
    const createdMillis =
      application.createdAt instanceof Date ? application.createdAt.getTime() : new Date(application.createdAt as any).getTime();
    return createdMillis > terminalAnchorMillis;
  });
  const userRank =
    currentCycleApplications.length > 0
      ? pendingApplications.findIndex(app => app.uid === user.uid) + 1
      : null;
	  // NOTE(policy): Portville 메인 stage 우선순위
	  // 1) in-progress residency (checkUserPortvilleStage에서 navigate 또는 lottery stage로 처리)
	  // 2) lottery(현재 회차 drawn)
	  // 3) application(현재 회차)
	  // 4) none
	  const effectiveStage: Exclude<UserPortvilleStage, 'checking'> =
	    userPortvilleStage === 'lottery' || userPortvilleStage === 'residency'
	      ? userPortvilleStage
	      : currentCycleApplications.length > 0
	        ? 'application'
	        : userPortvilleStage;

  return (
    <ClientLayout title="포트빌 입주 신청">
      <div className="flex flex-col min-h-screen bg-white">
        <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
          <div className="w-full max-w-[750px] px-4 sm:px-6">
            <h1 className="hidden lg:block text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-800 font-['Pretendard'] leading-9 mb-4">
              포트빌 입주 신청
            </h1>
            <p className="hidden lg:block text-base sm:text-lg md:text-xl font-normal text-slate-700 font-['Pretendard'] leading-9 mb-8">
              현재 대기현황을 확인하고 입주를 신청해주세요
            </p>
            <div className="hidden lg:block w-full h-px bg-gray-200 mb-8"></div>

            {effectiveStage !== 'none' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex flex-col gap-3">
                  <p className="text-blue-900 font-medium">
                    {effectiveStage === 'residency' && '입주 기록이 확인되었습니다.'}
                    {effectiveStage === 'lottery' && '추첨 이력이 확인되었습니다.'}
                    {effectiveStage === 'application' && '신청 이력이 확인되었습니다.'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    {effectiveStage === 'residency' && (
                      <button
                        onClick={() => navigate('/portville/residency')}
                        className="w-full sm:w-auto px-4 py-2 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold"
                      >
                        입주 기록 확인
                      </button>
                    )}
                    {effectiveStage === 'lottery' && (
                      <button
                        onClick={() => navigate('/portville/lottery')}
                        className="w-full sm:w-auto px-4 py-2 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold"
                      >
                        추첨 이력 확인
                      </button>
                    )}
                    {effectiveStage === 'application' && (
                      <button
                        onClick={() => navigate('/portville/detail')}
                        className="w-full sm:w-auto px-4 py-2 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold"
                      >
                        신청 이력 확인
                      </button>
                    )}
                    {effectiveStage === 'lottery' && lotteryStatus === 'drawn' && (
                      <button
                        onClick={() => navigate('/portville/contract')}
                        className="w-full sm:w-auto px-4 py-2 bg-white text-blue-700 rounded-sm outline outline-1 outline-blue-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold"
                      >
                        계약 진행하기
                      </button>
                    )}
                    {effectiveStage === 'lottery' && lotteryStatus === 'contracted' && (
                      <button
                        onClick={() => navigate('/portville/contract')}
                        className="w-full sm:w-auto px-4 py-2 bg-white text-blue-700 rounded-sm outline outline-1 outline-blue-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-bold"
                      >
                        계약 이력 확인
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {residencyHistory.length > 0 && (
              <div className="mb-8">
                <ResidencyHistorySection items={residencyHistory} />
              </div>
            )}

            {hasApprovedApplication ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 text-center">
                    승인되었습니다. 입주 계약을 진행해주세요.
                  </p>
                </div>
                <div className="space-y-4">
                  {effectiveStage === 'residency' ? (
                    <button
                      onClick={() => navigate('/portville/residency')}
                      className="w-full h-14 sm:h-16 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                    >
                      입주 기록 확인
                    </button>
                  ) : effectiveStage === 'lottery' ? (
                    <button
                      onClick={() => navigate('/portville/lottery')}
                      className="w-full h-14 sm:h-16 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                    >
                      추첨 이력 확인
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/portville/lottery')}
                      className="w-full h-14 sm:h-16 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                    >
                      호실 추첨하기
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/portville/detail')}
                    className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                  >
                    신청 내역 확인
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Pretendard'] leading-9">
                    현재 대기현황
                  </h2>
                  <button
                    onClick={handleExistingApplicationTransfer}
                    className="lg:hidden px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
                  >
                    기존 신청자 대기전환
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded border border-black/5 p-8 sm:p-12">
                  {waitingList.length === 0 ? (
                    <p className="text-center text-gray-500">현재 대기 중인 신청이 없습니다.</p>
                  ) : (
                    <div className="space-y-3 sm:space-y-4 mb-12">
                      {(() => {
                        const pendingApplications = waitingList
                          .filter(application => application.status === 'pending' || application.status === 'checked');
                        
                        // 사용자의 신청 건 찾기
                        const userApplicationIndex = pendingApplications.findIndex(app => app.uid === user.uid);
                        
                        // 표시할 신청 건 필터링
                        let displayApplications: (typeof pendingApplications[0] | null)[] = pendingApplications;
                        if (pendingApplications.length > 5) {
                          if (userApplicationIndex === -1 || 
                              userApplicationIndex < 2 || 
                              userApplicationIndex >= pendingApplications.length - 3) {
                            // 사용자의 신청이 없거나, 처음 2건 또는 마지막 3건에 포함된 경우
                            displayApplications = [
                              ...pendingApplications.slice(0, 2),
                              null, // 생략 표시를 위한 null
                              ...pendingApplications.slice(-3)
                            ];
                          } else {
                            // 사용자의 신청이 중간에 있는 경우
                            displayApplications = [
                              ...pendingApplications.slice(0, 2),
                              null, // 생략 표시를 위한 null
                              ...pendingApplications.slice(userApplicationIndex - 2, userApplicationIndex + 1)
                            ];
                          }
                        }

                        return displayApplications.map((application, index) => {
                          if (application === null) {
                            // 생략 표시
                            return (
                              <div key="ellipsis" className="flex justify-center py-2">
                                <span className="text-gray-400 text-lg">···</span>
                              </div>
                            );
                          }

                          const originalIndex = pendingApplications.findIndex(app => app.id === application.id);
                          return (
                            <div key={application.id} 
                                 className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 bg-white rounded-sm outline outline-1 outline-slate-200">
                              <div className="flex items-center gap-4 sm:gap-6 mb-2 sm:mb-0">
                                <div className="flex items-center gap-3">
                                  <span className="text-lg sm:text-xl font-semibold text-slate-800 font-['Pretendard'] leading-9">
                                    대기 {originalIndex + 1}번
                                  </span>
                                  {application.uid === user.uid && (
                                    <span className="px-3 py-1.5 bg-green-100 text-green-800 rounded text-sm font-medium">
                                      내 신청
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <svg 
                                  className="w-6 h-6 text-sky-500" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path 
                                    d="M12 8V12L15 15" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  />
                                  <circle 
                                    cx="12" 
                                    cy="12" 
                                    r="9" 
                                    stroke="currentColor" 
                                    strokeWidth="2"
                                  />
                                </svg>
                                <span className="text-base sm:text-lg font-medium text-sky-500 font-['Pretendard'] leading-9">
                                  {formatDate(application.createdAt)}
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                  <div className="space-y-4">
                    {hasPendingApplication ? (
                      <button
                        onClick={() => navigate('/portville/detail')}
                        className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                      >
                        신청 내역 확인
                      </button>
                    ) : (
                      <div className="space-y-4">
                        <button
                          onClick={() => navigate('/portville/waiting')}
                          className="w-full h-16 sm:h-20 bg-blue-700 text-white rounded-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-lg sm:text-xl font-bold font-['Pretendard'] leading-9"
                        >
                          입주 신청하기
                        </button>
                        <button
                          onClick={() => navigate('/portville/code')}
                          className="w-full h-16 sm:h-20 bg-white text-gray-700 rounded-sm outline outline-1 outline-slate-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-lg sm:text-xl font-bold font-['Pretendard'] leading-9"
                        >
                          코드로 신청하기
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default PortvillePage;
